import apiLmsConteudo from '../../../services/apiLmsConteudo';
import actionsSlide from '../../../actions/Slide';

const MIME_BY_EXT = {
  jpg: 'image/jpeg', jpeg: 'image/jpeg',
  png: 'image/png', gif: 'image/gif',
  webp: 'image/webp', svg: 'image/svg+xml',
  bmp: 'image/bmp', tiff: 'image/tiff',
  mp4: 'video/mp4', webm: 'video/webm',
  pdf: 'application/pdf',
};

function resolveContentType(file) {
  if (file.type) return file.type;
  const ext = file.name.split('.').pop().toLowerCase();
  return MIME_BY_EXT[ext] || 'application/octet-stream';
}

function getFileName(file) {
  const fileNameSplitted = file.name.split('.');

  if (fileNameSplitted.length > 1) {
    fileNameSplitted.pop();
    return fileNameSplitted.join('.');
  }

  return fileNameSplitted[0];
}

export default async function upload(
  file,
  nome = null,
  categoria = null,
  aulaId = null,
  onUploadProgress,
  onProcessing
) {
  const fileType = file.type.split('/')[0];
  const fileSizeKb = file.size / 1000;

  if (nome === null) {
    nome = getFileName(file);
  }

  if (fileType === 'image' && fileSizeKb > 1024) {
    return Promise.reject(
      new Error('Não são permitidas imagens maiores que 1MB.')
    );
  }

  return new Promise((resolve, reject) => {
    uploadFile(file, aulaId, categoria, onUploadProgress, onProcessing)
      .then(data => {
        const tipo = file.type.split('/')[0];
        const saveAssetData = {
          url: data.url,
          categoria_id: categoria ? categoria.id : null,
          tipo,
          nome
        };
        apiLmsConteudo
          .post('/assets', saveAssetData)
          .then(() => {
            resolve(data);
          })
          .catch(err => reject(err));
      })
      .catch(err => {
        reject(err);
      });
  });
}

function getFileNameUpload(file) {
  try {
    const fileNameSplitted = file.name.split('.');
    const fileName =
      Date.now() + '.' + fileNameSplitted[fileNameSplitted.length - 1];
    return fileName;
  } catch (error) {
    return file.name;
  }
}

/**
 * Upload direto ao S3 via URL pré-assinada.
 * 1) Pede ao servidor uma URL pré-assinada
 * 2) Faz PUT do arquivo direto no S3
 * 3) Confirma no servidor para obter url/relativeUrl e cópia para aula se necessário
 */
async function uploadFile(file, aulaId, categoria, onUploadProgress, onProcessing) {
  const fileName = getFileNameUpload(file);

  const { data: presignedData } = await apiLmsConteudo.post(
    '/presigned-upload-url',
    {
      fileName,
      fileSize: file.size,
      contentType: resolveContentType(file),
      categoria: categoria?.nome ?? null,
      aulaId: aulaId ?? null,
    }
  );

  if (presignedData.error) {
    throw new Error(presignedData.error);
  }

  const { uploadUrl, keyname } = presignedData;

  await putFileToS3(uploadUrl, file, onUploadProgress);

  const { data: confirmData } = await apiLmsConteudo.post('/confirm-upload', {
    keyname,
    fileName,
    aulaId: aulaId ?? null,
    categoria: categoria?.nome ?? null,
  });

  if (confirmData.error) {
    throw new Error(confirmData.error);
  }

  // Vídeo: NÃO bloqueia mais. A compressão (H.264 + AAC) roda no worker via fila.
  // O bloco é inserido no slide já em "processing" (sem url); quem dispara a
  // compressão com o contexto do slide é quem insere o bloco (startVideoCompression),
  // e o worker atualiza a url no banco ao terminar. Devolvemos processing=true e a
  // url pública do S3 só para registrar o asset na galeria (o bloco não a usa ainda).
  if (confirmData.processing) {
    const relativeUrl = confirmData.relativeUrl ?? keyname;
    return { url: publicS3Url(relativeUrl), relativeUrl, processing: true };
  }

  return { url: confirmData.url, relativeUrl: confirmData.relativeUrl };
}

/**
 * Enfileira a compressão do vídeo já com o contexto do slide (slideId + blockKey).
 * Chamar DEPOIS de inserir o bloco e salvar o slide. Best-effort.
 */
export async function startVideoCompression(keyname, slideId, blockKey) {
  try {
    await apiLmsConteudo.post('/video/start-compression', {
      keyname,
      slideId,
      blockKey
    });
  } catch (e) {
    // fila indisponível não pode travar o editor
  }
}

// keynames sendo "pollados" agora — evita polls duplicados quando o mesmo bloco
// re-renderiza (drag/resize) ou quando o slide re-monta (reload / trocar de slide).
const activePolls = new Set();

/**
 * Poll leve e NÃO-bloqueante de /upload-status. Quando o vídeo fica pronto, atualiza
 * só o redux local (seta a url + limpa "processing" no bloco) — o backend já
 * persistiu no banco. Não re-salva o slide. Idempotente por keyname.
 */
export function pollVideoProcessing(
  keyname,
  blockKey,
  dispatch,
  { intervalMs = 4000, timeoutMs = 30 * 60 * 1000 } = {}
) {
  if (!keyname || activePolls.has(keyname)) return;
  activePolls.add(keyname);

  const start = Date.now();
  const tick = async () => {
    if (Date.now() - start > timeoutMs) {
      activePolls.delete(keyname);
      return;
    }
    try {
      const { data } = await apiLmsConteudo.post('/upload-status', { keyname });
      if (data.ready && data.url) {
        dispatch(actionsSlide.setBlockProcessed(blockKey, data.url));
        activePolls.delete(keyname);
        return;
      }
    } catch (e) {
      // erro transitório -> tenta de novo
    }
    setTimeout(tick, intervalMs);
  };
  tick();
}

// URL pública do objeto no S3 (mesmo formato do backend).
function publicS3Url(keyname) {
  return 'https://rockfeller.s3-sa-east-1.amazonaws.com/' + keyname.replace(/ /g, '%20');
}

/**
 * Envia o arquivo via PUT para a URL pré-assinada do S3 (com suporte a progresso).
 */
function putFileToS3(uploadUrl, file, onUploadProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (e) => {
      if (e.lengthComputable && onUploadProgress) {
        onUploadProgress((e.loaded / e.total) * 100);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload falhou: ${xhr.status} ${xhr.statusText}`));
      }
    });

    xhr.addEventListener('error', () => reject(new Error('Erro de rede no upload')));
    xhr.addEventListener('abort', () => reject(new Error('Upload cancelado')));

    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', resolveContentType(file));
    xhr.send(file);
  });
}
